/**
 * UserLinks.tsx — Account / Login Links
 *
 * Variants (data.content.variant):
 *   icon-only      — person silhouette, no label.
 *   icon-text      — person icon + "Account" / "Sign in" label.
 *   avatar-name    — when logged-in, small avatar circle with initials + first name; falls back to icon-text for guests.
 *   pill-dropdown  — pill with chevron, always reveals a dropdown menu (orders / wishlist / sign out).
 *
 * Legacy: `bento-dark` and `solid_box` variants preserved for backwards compatibility.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { openAuthModal } from '../../account/authModalStore';

interface UserLinksProps {
  data: {
    showLogin?: boolean;
    showRegister?: boolean;
    showAccount?: boolean;
    showWishlist?: boolean;
    showLabels?: boolean;
    variant?: string;
    content?: { variant?: string };
    settings?: {
      pillBackground?: string;
      pillBorder?: string;
      pillRadius?: string;
      pillPadding?: string;
    };
    [key: string]: unknown;
  };
  styles?: string;
  variant?: string;
}

type Layout = 'icon-only' | 'icon-text' | 'avatar-name' | 'pill-dropdown';

const PersonIcon: React.FC<{ stroke?: string; strokeWidth?: number }> = ({ stroke, strokeWidth = 1.5 }) => (
  <svg
    style={{
      width: 'var(--style-header-icon-size, 22px)',
      height: 'var(--style-header-icon-size, 22px)',
    }}
    fill="none" viewBox="0 0 24 24" stroke={stroke ?? 'var(--icon-color, currentColor)'}
  >
    <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={strokeWidth} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const ChevronIcon: React.FC<{ open?: boolean }> = ({ open }) => (
  <svg
    className="w-3 h-3 transition-transform"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const initialsFromName = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const UserLinks: React.FC<UserLinksProps> = ({ data, styles, variant }) => {
  const {
    showLogin = true,
    showAccount = true,
    showLabels = true,
  } = data ?? {};

  const effectiveVariant = variant || data?.variant;
  const isSolidBox = effectiveVariant === 'solid_box';

  // Resolve new-style layout variant.
  const variantRaw = data?.content?.variant
    ?? (typeof effectiveVariant === 'string' ? effectiveVariant : undefined)
    ?? (showLabels ? 'icon-text' : 'icon-only');
  const layoutVariant: Layout =
    (['icon-only', 'icon-text', 'avatar-name', 'pill-dropdown'] as const).includes(variantRaw as Layout)
      ? variantRaw as Layout
      : (showLabels ? 'icon-text' : 'icon-only');

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number; right: number } | null>(null);

  // Compute portal coords + refresh on resize/scroll
  useEffect(() => {
    if (!showAccountMenu) return;
    const update = () => {
      const el = menuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showAccountMenu]);

  useEffect(() => {
    // Read the canonical auth-store keys (magento-customer-token / -data).
    // The previous keys (customer_token / customer_name) never matched what the
    // auth store writes, so isLoggedIn stayed false on every load and the header
    // icon always opened the login modal instead of the account menu.
    const token = localStorage.getItem('magento-customer-token');
    if (token) {
      setIsLoggedIn(true);
      try {
        const raw = localStorage.getItem('magento-customer-data');
        if (raw) {
          const c = JSON.parse(raw) as { firstname?: string; lastname?: string };
          const name = [c?.firstname, c?.lastname].filter(Boolean).join(' ').trim();
          if (name) setCustomerName(name);
        }
      } catch { /* name is best-effort; logged-in state already set */ }
    }

    interface AuthChangeDetail { isLoggedIn?: boolean; name?: string }
    const handleAuthChange = (e: Event) => {
      const detail = (e as CustomEvent<AuthChangeDetail>).detail;
      setIsLoggedIn(!!detail?.isLoggedIn);
      setCustomerName(detail?.name ?? '');
    };
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  // Outside click for dropdown — include the portaled dropdown ref so clicks
  // inside it don't immediately close the menu.
  useEffect(() => {
    if (!showAccountMenu) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = menuRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAccountMenu]);

  const handleLogout = () => {
    localStorage.removeItem('magento-customer-token');
    localStorage.removeItem('magento-customer-data');
    setIsLoggedIn(false);
    setCustomerName('');
    window.dispatchEvent(new CustomEvent('auth-change', { detail: { isLoggedIn: false } }));
    window.location.href = '/';
  };

  const handleOpenAuth = (e: React.MouseEvent) => {
    e.preventDefault();
    openAuthModal();
  };

  // Chrome-scoped tokens — when this account icon sits in the header
  // brand/utility row, prefer the chrome --color-header-icon stroke; fall
  // back to body --text-* for non-chrome themes. Hover lifts the chrome
  // icon to a slightly brighter shade or to the body --text-primary.
  // The account pill's resting+hover appearance comes from the WRAPPER element,
  // which carries .header-icon-btn (see the variant renders below). The inner
  // <button> must NOT also be a .header-icon-btn — it sits inside the pill with
  // an inline background/border/padding reset, so doubling the class produced a
  // nested-pill / bare-button mismatch. Keep the inner button layout-only; the
  // wrapper's .header-icon-btn drives colour/bg/border/hover for consistency
  // with every other header icon.
  const baseClass = `flex items-center gap-1.5 transition-all duration-200`;

  const {
    pillBackground = 'var(--color-header-icon-bg, rgba(255,255,255,0.06))',
    pillBorder = '1px solid var(--color-header-icon-border, var(--glass-border, rgba(255,255,255,0.18)))',
    pillRadius = 'var(--style-btn-radius, 9999px)',
    pillPadding = '4px 10px',
  } = data?.settings ?? {};

  let pillStyle: React.CSSProperties = {
    backgroundColor: pillBackground,
    border: pillBorder,
    borderRadius: pillRadius,
    padding: layoutVariant === 'icon-only' ? pillPadding : '4px 14px',
    minHeight: '32px',
    transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
  };

  if (isSolidBox) {
    pillStyle = {
      backgroundColor: 'var(--surface-1)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--style-btn-radius, 4px)',
      padding: '8px 16px',
      minHeight: '44px',
      // Shadow tracks --style-btn-primary-shadow — replaces the dead
      // Tailwind `shadow-sm` that used to sit on the wrapper className.
      boxShadow: 'var(--style-btn-primary-shadow, 0 1px 2px 0 rgba(0,0,0,0.05))',
    };
  }

  // Dropdown menu (shared by avatar-name + pill-dropdown when logged-in) —
  // portaled to document.body so it escapes header / nav stacking contexts.
  const dropdownMenu = (showAccountMenu && dropdownCoords && typeof document !== 'undefined') ? createPortal(
    <div
      ref={dropdownRef}
      // shadow-xl removed — inline boxShadow below already drives this via
      // --style-modal-shadow with higher specificity.
      className="overflow-hidden"
      style={{
        position: 'fixed',
        top: dropdownCoords.top,
        right: dropdownCoords.right,
        width: 192,
        zIndex: 99999,
        borderRadius: 'var(--style-modal-radius, var(--style-card-radius, 12px))',
        backgroundColor: 'var(--style-modal-bg, var(--surface-2))',
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--style-modal-shadow, 0 12px 40px rgba(0,0,0,0.2))',
      }}
    >
      <a href="/account" className="block px-4 py-3 text-sm hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,transparent)] transition-colors" style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
        My Account
      </a>
      <a href="/account/orders" className="block px-4 py-3 text-sm hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,transparent)] transition-colors" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
        Orders
      </a>
      <a href="/account/wishlist" className="block px-4 py-3 text-sm hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,transparent)] transition-colors" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
        Wishlist
      </a>
      <div style={{ borderTop: '1px solid var(--glass-border)' }} />
      <button
        onClick={handleLogout}
        className="block w-full text-left px-4 py-3 text-sm hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,transparent)] transition-colors"
        style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
      >
        Sign Out
      </button>
    </div>,
    document.body
  ) : null;

  // ------------------------------------------------------------------
  // Variant: avatar-name (when logged-in)
  // ------------------------------------------------------------------
  if (layoutVariant === 'avatar-name' && isLoggedIn) {
    const initials = initialsFromName(customerName);
    const firstName = customerName.trim().split(/\s+/)[0] || 'Account';
    return (
      <div
        ref={menuRef}
        className={`relative flex items-center gap-2 ${styles ?? ''}`}
        style={pillStyle}
        data-variant="avatar-name"
      >
        <button
          onClick={() => setShowAccountMenu(o => !o)}
          className="flex items-center gap-2"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
          aria-label={customerName || 'My Account'}
          aria-haspopup="menu"
          aria-expanded={showAccountMenu}
        >
          <span
            className="flex items-center justify-center text-[10px] font-bold uppercase"
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'color-mix(in srgb, var(--accent-primary) 22%, transparent)',
              color: 'var(--color-accent-fg)',
              border: '1px solid color-mix(in srgb, var(--accent-primary) 35%, transparent)',
              letterSpacing: '0.04em',
            }}
          >
            {initials}
          </span>
          <span className="text-[12px] font-medium hidden sm:inline">
            {/* No inline color — inherits from parent .header-icon-btn so :hover
                cascades down (inline color would block the hover state). */}
            {firstName}
          </span>
          <span className="hidden sm:inline" style={{ opacity: 0.7 }}>
            {/* Use opacity instead of inline color-mix so :hover from parent still wins. */}
            <ChevronIcon open={showAccountMenu} />
          </span>
        </button>
        {showAccountMenu && dropdownMenu}
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Variant: pill-dropdown (logged-in dropdown OR sign-in pill)
  // ------------------------------------------------------------------
  if (layoutVariant === 'pill-dropdown') {
    if (isLoggedIn) {
      return (
        <div
          ref={menuRef}
          className={`relative flex items-center gap-3 ${styles ?? ''}`}
          style={pillStyle}
          data-variant="pill-dropdown"
        >
          <button
            onClick={() => setShowAccountMenu(o => !o)}
            className={`${baseClass} group`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            aria-haspopup="menu"
            aria-expanded={showAccountMenu}
          >
            <PersonIcon />
            <span className="text-[12px] font-medium hidden sm:inline">
              {customerName ? customerName.split(/\s+/)[0] : 'Account'}
            </span>
            <ChevronIcon open={showAccountMenu} />
          </button>
          {showAccountMenu && dropdownMenu}
        </div>
      );
    }
    return (
      <div className={`flex items-center gap-3 ${styles ?? ''} header-icon-btn`} style={pillStyle} data-variant="pill-dropdown">
        {showLogin && (
          <button
            onClick={handleOpenAuth}
            className={`${baseClass} group`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            title="Sign In"
            aria-label="Sign In"
          >
            <PersonIcon />
            <span className="text-[12px] font-medium">Sign in</span>
            <ChevronIcon />
          </button>
        )}
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Variant: icon-only / icon-text / avatar-name-guest fallback
  // ------------------------------------------------------------------
  const showLabel = layoutVariant !== 'icon-only';
  return (
    <div
      className={`flex items-center gap-3 ${isSolidBox ? 'hover:bg-[var(--surface-2)]' : 'header-icon-btn'} ${styles ?? ''}`}
      style={pillStyle}
      data-variant={layoutVariant}
      onMouseEnter={e => { if (!isSolidBox) e.currentTarget.style.boxShadow = '0 0 16px rgba(var(--accent-primary-rgb, 59,130,246), 0.35)'; }}
      onMouseLeave={e => { if (!isSolidBox) e.currentTarget.style.boxShadow = 'none'; }}
    >
      {isLoggedIn ? (
        showAccount && (
          <div
            ref={menuRef}
            className="relative"
          >
            <button
              // Opens the account menu on CLICK (not hover). Hover dropdowns are
              // unreliable on touch-laptops and worse for a11y; click is the
              // top-tier-store standard. Outside-click closes it (see the
              // showAccountMenu useEffect). When signed in, the person icon is
              // replaced by an initials avatar as a clear logged-in cue.
              onClick={() => setShowAccountMenu(o => !o)}
              className={`${baseClass} group ${!showLabel && !isSolidBox ? 'w-8 h-8 rounded-[inherit] flex items-center justify-center' : ''}`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: (!showLabel && !isSolidBox) ? 0 : undefined, color: isSolidBox ? 'var(--text-primary)' : undefined }}
              title={customerName || 'My Account'}
              aria-label={customerName || 'My Account'}
              aria-haspopup="menu"
              aria-expanded={showAccountMenu}
            >
              {customerName ? (
                <span
                  className="flex items-center justify-center text-[10px] font-bold uppercase"
                  style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'color-mix(in srgb, var(--accent-primary) 22%, transparent)',
                    color: 'var(--color-accent-fg)',
                    border: '1px solid color-mix(in srgb, var(--accent-primary) 35%, transparent)',
                    letterSpacing: '0.04em', flex: '0 0 auto',
                  }}
                >
                  {initialsFromName(customerName)}
                </span>
              ) : (
                <PersonIcon stroke={isSolidBox ? 'currentColor' : undefined} strokeWidth={isSolidBox ? 2 : 1.5} />
              )}
              {(showLabel || isSolidBox) && (
                <span className={`text-[12px] ${isSolidBox ? 'text-[14px] font-bold uppercase tracking-wide' : 'font-medium'} hidden sm:inline`}>
                  {customerName ? customerName.split(/\s+/)[0] : 'Account'}
                </span>
              )}
            </button>
            {showAccountMenu && dropdownMenu}
          </div>
        )
      ) : (
        showLogin && (
          <button
            onClick={handleOpenAuth}
            // 2026-05-21 — same fix as the logged-in button above. Inline
            // style.color already drives the colour via --text-primary;
            // text-black + hover:text-[var(--primary)] (note: --primary
            // doesn't even exist — should have been --accent-primary) were
            // dead Tailwind classes that prevented theme customisation.
            className={`${baseClass} group ${!showLabel && !isSolidBox ? 'w-8 h-8 rounded-[inherit] flex items-center justify-center' : ''}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: (!showLabel && !isSolidBox) ? 0 : undefined, color: isSolidBox ? 'var(--text-primary)' : undefined }}
            title="Sign In"
            aria-label="Sign In"
          >
            <PersonIcon stroke={isSolidBox ? 'currentColor' : undefined} strokeWidth={isSolidBox ? 2 : 1.5} />
            {(showLabel || isSolidBox) && (
              <span className={`text-[12px] ${isSolidBox ? 'font-bold uppercase tracking-wide text-[13px]' : 'font-medium'} hidden sm:inline`}>Account</span>
            )}
          </button>
        )
      )}
    </div>
  );
};

export default UserLinks;
